import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { numberInputChecking } from '../../utils/global'
import Flex from '../Flex'
import Text from '../Text'
import { NumberInputProps } from './Input.types'
import { InputWrapper, PlainInput } from './styled'

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    { min, max, onChange, value = 0, ...restProps }: NumberInputProps,
    ref: React.MutableRefObject<HTMLInputElement>,
  ) => {
    const [tempValue, setTempValue] = useState<string>(
      (min && min.toString()) || value
        ? numberInputChecking(value.toString(), { min, max }).formattedValue
        : '',
    )

    useEffect(() => {
      setTempValue(
        (min && min.toString()) || value
          ? numberInputChecking(value.toString(), { min, max }).formattedValue
          : '',
      )
    }, [min, max, value])

    const onNumberFormatChange: React.FormEventHandler<HTMLInputElement> = (
      event: FormEvent<HTMLInputElement>,
    ) => {
      const value = event.currentTarget.value || '0'

      const { formattedValue, parsedValue } = numberInputChecking(value, {
        min,
        max,
      })

      setTempValue(formattedValue)
      onChange && onChange(parsedValue)
    }

    return <PlainInput value={tempValue} onChange={onNumberFormatChange} ref={ref} {...restProps} />
  },
)

const InputNumber = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    { prefix, sufix, label, hint, isError, size = 'regular', error, disabled, ...restProps },
    ref,
  ) => {
    const inputRef =
      (ref as React.MutableRefObject<HTMLInputElement>) || useRef<HTMLInputElement>(null)
    // hard to reproduce
    /* istanbul ignore next */
    const onClickInput = () => {
      if (inputRef.current) inputRef.current.focus()
    }

    const renderErrorText = () => {
      if (isError && error) {
        return (
          <Text variant='caption' color='red50' aria-label='input-error'>
            {error}
          </Text>
        )
      }
      if (hint) {
        return (
          <Text variant='caption' color='black50' aria-label='input-hint'>
            {hint}
          </Text>
        )
      }
    }

    return (
      <Flex flexDirection='column' rowGap='8px' width='100%'>
        {label && (
          <Text variant='caption' weight='bold' color='black50' aria-label='label-input-number'>
            {label}
          </Text>
        )}
        {prefix || sufix ? (
          <InputWrapper
            aria-label='input-number-wrapper'
            size={size}
            isError={isError}
            onClick={onClickInput}
            disabled={disabled}
            style={{
              backgroundColor: restProps?.style?.backgroundColor ?? 'none',
            }}
          >
            {prefix}
            <NumberInput
              disabled={disabled}
              isPlain
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ref={inputRef as any}
              size={size}
              isError={isError}
              {...restProps}
            />
            {sufix}
          </InputWrapper>
        ) : (
          <NumberInput
            disabled={disabled}
            size={size}
            isError={isError}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ref={inputRef as any}
            {...restProps}
          />
        )}
        {renderErrorText()}
      </Flex>
    )
  },
)

export default InputNumber
